import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Newspaper } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { API_URL } from "../data/site";

export default function Recaps() {
  const [recaps, setRecaps] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/recaps`)
      .then((res) => setRecaps(res.data))
      .catch(() => setRecaps([]));
  }, []);

  return (
    <div data-testid="recaps-page">
      <PageHeader
        overline="Race Weekend Recaps"
        lines={["Results built", "on preparation."]}
        description="Completed events, real outcomes. Each recap covers a weekend the ABS LLC team ran — the circuit, the operation, and the result."
      />

      <section data-testid="recaps-list" className="border-t border-neutral-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          {recaps === null ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-[#e63946]" />
            </div>
          ) : recaps.length === 0 ? (
            <div className="flex flex-col items-center gap-4 border border-dashed border-neutral-300 py-20 text-neutral-400">
              <Newspaper className="h-8 w-8" />
              <p className="text-sm">The first recap is being written — check back after the next race weekend.</p>
            </div>
          ) : (
            recaps.map((r, i) => (
              <Reveal key={r.id}>
                <article
                  data-testid={`recap-card-${i}`}
                  className={`grid gap-10 border-t border-neutral-200 py-14 md:grid-cols-12 md:py-20 ${i === recaps.length - 1 ? "border-b" : ""}`}
                >
                  <div className="md:col-span-5">
                    <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                      {r.circuit}
                    </p>
                    <h2 className="font-display mt-4 text-2xl font-bold tracking-tight text-[#111111] md:text-4xl">
                      {r.title}
                    </h2>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {r.event && (
                        <span className="bg-[#f8f9fa] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600">
                          {r.event}
                        </span>
                      )}
                      <span className="bg-[#f8f9fa] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-600">
                        {r.date_label}
                      </span>
                      {r.result && (
                        <span className="bg-[#111111] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white">
                          {r.result}
                        </span>
                      )}
                    </div>
                    <p className="mt-6 text-base leading-relaxed text-neutral-600">{r.summary}</p>
                  </div>
                  {r.images.length > 0 && (
                    <div className="md:col-span-7">
                      <div className="clip-corner img-treatment overflow-hidden">
                        <img
                          src={r.images[0]}
                          alt={r.title}
                          className="aspect-[16/10] w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      {r.images.length > 1 && (
                        <div className="mt-4 grid grid-cols-3 gap-4">
                          {r.images.slice(1, 4).map((img, j) => (
                            <div key={j} className="img-treatment overflow-hidden">
                              <img
                                src={img}
                                alt={`${r.title} — photo ${j + 2}`}
                                className="aspect-[4/3] w-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              </Reveal>
            ))
          )}
        </div>
      </section>

      <CtaBand title="Your weekend could be next." accent="Let's plan it." />
    </div>
  );
}
